import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, Card, Skeleton, Typography } from "@mui/material";

// components
import TokenLogo from "../../components/TokenLogo";
import StatsRow, { FigureText } from "../../components/StatsRow";
import ExploreTable from "../../components/ExploreTable";
import StatusPage from "../../components/StatusPage";

// data
import { useExplorePools, useExploreToken } from "../../hooks/explore";
import { amountType } from "../../theme/main";
import { formatAmount, formatCount, formatPriceUSD, formatUSD } from "../../utils/format";
import { formatVolume, poolColumns, poolHref } from "./columns";
import { AddressRow, contractAddress, DetailError, ExploreCrumbs, Section, useStartSwap } from "./detail";

const TokenPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = useExploreToken(id);
  const pools = useExplorePools();
  const { canSwap, startSwap } = useStartSwap();
  const tokenPools = useMemo(
    () => (pools.data || []).filter((pool) => pool.token0.id === id || pool.token1.id === id),
    [pools.data, id]
  );
  const data = token.data;

  if (token.notFound) {
    return (
      <StatusPage
        title="Token not found"
        actions={
          <Button variant="contained" onClick={() => navigate("/explore/tokens")}>
            Back to tokens
          </Button>
        }
      >
        No KoinDX pool holds a token with this address.
      </StatusPage>
    );
  }

  return (
    <Box sx={{ maxWidth: 960, mx: "auto", pb: { xs: 10, md: 6 } }}>
      <ExploreCrumbs section="Tokens" href="/explore/tokens" current={data ? data.symbol : "…"} />
      {token.isError ? <DetailError what="token" onRetry={token.refetch} /> : null}

      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2, mb: 3 }}>
        {data ? <TokenLogo token={data} size={48} /> : <Skeleton variant="circular" width={48} height={48} />}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="h4" component="h1" noWrap>
            {data ? data.name : <Skeleton width={180} />}
          </Typography>
          <Typography sx={{ color: "text.secondary" }}>{data ? data.symbol : <Skeleton width={60} />}</Typography>
        </Box>
        {data && canSwap(data.id) ? (
          <Button variant="contained" size="large" onClick={() => startSwap(null, data.id)}>
            Swap
          </Button>
        ) : null}
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{ ...amountType, fontVariantNumeric: "proportional-nums", fontSize: { xs: "2.25rem", md: "3rem" } }}
        >
          {data ? <FigureText>{formatPriceUSD(data.priceUSD)}</FigureText> : <Skeleton width={200} />}
        </Typography>
        {data && data.id !== "koin" && Number(data.derivedKoin) > 0 ? (
          <Typography sx={{ color: "text.secondary" }}>
            1 {data.symbol} = {formatAmount(data.derivedKoin, { maxDecimals: 8 })} KOIN
          </Typography>
        ) : null}
        {data && data.priceUSD === null ? (
          <Typography sx={{ color: "text.secondary" }}>
            No pool links this token to KOIN, so it has no USD price.
          </Typography>
        ) : null}
      </Box>

      <StatsRow
        items={[
          { label: "TVL", value: data ? formatUSD(data.totalLiquidityUSD) : null },
          { label: "Total volume", value: data ? formatVolume(data.tradeVolumeUSD, data.txCount) : null },
          { label: "Transactions", value: data ? formatCount(data.txCount) : null },
          { label: "Pools", value: pools.data ? formatCount(tokenPools.length) : null },
        ]}
      />

      <Section title={data ? `Pools with ${data.symbol}` : "Pools"}>
        <Card>
          <ExploreTable
            label={data ? `Pools with ${data.symbol}` : "Pools"}
            columns={poolColumns}
            rows={tokenPools}
            rowHref={poolHref}
            initialSort={{ id: "tvl", order: "desc" }}
            loading={pools.isLoading}
            emptyText="This token has no pool with USD liquidity."
          />
        </Card>
      </Section>

      {data ? (
        <Section title="Details">
          <Card sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
            <AddressRow label="Contract" address={contractAddress(data.id)} />
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1.5 }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                In pools
              </Typography>
              <Typography variant="body2">
                {formatAmount(data.totalLiquidity, { maxDecimals: 4 })} {data.symbol}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1.5 }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Decimals
              </Typography>
              <Typography variant="body2">{data.decimals}</Typography>
            </Box>
          </Card>
        </Section>
      ) : null}
    </Box>
  );
};

export default TokenPage;
